// src/encounter/complaint-map/system-order-for.ts
// PROD-2789 — the coded chief complaint SUGGESTS which ROS / exam systems open first.
//
// Samer's rule A9: appointment type + specialty pick the note. The complaint never selects,
// hides or answers anything. It only hands the DS `QuestionnaireRenderer` an ordered list of
// system keys (`systemOrder`, scrips-signal-ds #423), and the renderer moves those groups to the
// front. Every other group stays, in its authored order.
//
// So this module answers one question: "for this step and these complaints, which keys?"
//   · only 'ros' and 'pe' ever get keys; every other step gets `undefined`;
//   · the FIRST complaint that has a map row wins;
//   · a complaint finds its row by its CODE (the catalogue id). Only when the code is EMPTY does
//     it fall back to its saved text, and then only on an EXACT match (trimmed, case-insensitive)
//     against the row's catalogue name. No fuzzy, partial or stemmed matching. A non-empty code
//     that matches no row does not fall back: the code wins (agreed with Templates, 2026-09-30).
//     The fallback exists because the app's own picker saves a complaint with an empty code;
//   · no complaint, an unknown code or name, or no row → `undefined`, which the renderer reads as
//     "the authored order". The ids are DEV catalogue ids that were not re-read against the
//     database, so a miss is expected and must stay silent.
//
// The data is `complaint-map-v2.json` beside this file: a draft, not clinically signed off (A10).
// v2 (2026-10-05) keeps every v1 row's keys unchanged and adds the complaints the adult primary-care
// and nurse phrase libraries cover. Each row also carries `phrasesFirst` (library -> note editor ->
// phrase triggers to offer first), `phraseGates` and its sources; this module reads only the keys.
import type { PaStepKey } from '../lib/encounter-steps';
import { isComplaintEntry, type PatientComplaintModel } from '../lib/to-chief-complaint';
import complaintMap from './complaint-map-v2.json';

export interface ComplaintMapRow {
  catalogueId: string;
  /** The catalogue name, as the DEV catalogue spells it. */
  display: string;
  ros: readonly string[];
  pe: readonly string[];
  /** PROD-2947 Part 2: library → note editor → phrase triggers to offer FIRST (a menu order, never an insert). */
  phrasesFirst?: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>>;
  /** A library offered first only from this age (e.g. the nurse's falls phrases from 65). */
  phraseGates?: Readonly<Record<string, { minAgeYears: number }>>;
  /** PROD-2947 Part 2: the starter-map presentation (pcp-adult) and nurse visit reason this complaint opens. */
  phraseLinks?: { pcpPresentation?: string; nurseVisitReason?: string };
}

/** One complaint as the encounter saved it: its catalogue code and its text. */
export interface ChiefComplaintRef {
  code?: string | null;
  display?: string | null;
}

/** A GUID is case-insensitive (the map holds them upper-case, a .NET serialiser writes them
 *  lower-case), and the name match is case-insensitive by agreement. Trim, then lower-case. Nothing
 *  else: no whitespace collapsing, no punctuation stripping, no stemming. */
const normalise = (value: string): string => value.trim().toLowerCase();

const ROWS = complaintMap.rows as unknown as ComplaintMapRow[];
const ROWS_BY_ID: ReadonlyMap<string, ComplaintMapRow> = new Map(ROWS.map((row) => [normalise(row.catalogueId), row]));
const ROWS_BY_DISPLAY: ReadonlyMap<string, ComplaintMapRow> = new Map(ROWS.map((row) => [normalise(row.display), row]));

/** The map row for one complaint: by code; by exact name only when the code is empty. */
export function rowFor(complaint: ChiefComplaintRef): ComplaintMapRow | undefined {
  const code = complaint.code?.trim();
  if (code) return ROWS_BY_ID.get(normalise(code));
  const display = complaint.display?.trim();
  return display ? ROWS_BY_DISPLAY.get(normalise(display)) : undefined;
}

/**
 * The `systemOrder` keys for the step, from the first complaint that has a map row.
 *
 * The array returned is the map's own, so the same row always yields the same reference and a
 * re-render with unchanged complaints hands the renderer an identical prop.
 */
export function systemOrderFor(
  step: PaStepKey,
  chiefComplaints: readonly ChiefComplaintRef[] | undefined,
): readonly string[] | undefined {
  for (const complaint of chiefComplaints ?? []) {
    const row = rowFor(complaint);
    if (!row) continue;
    const keys = step === 'ros' ? row.ros : step === 'pe' ? row.pe : undefined;
    return keys && keys.length > 0 ? keys : undefined;
  }
  return undefined;
}

/**
 * Each complaint on the encounter's complaint document as {code, display}, in document order.
 *
 * The code is `coding.code` (Scrips.Patient stores it verbatim, `CreatePatientComplaintCommand`),
 * the text is `coding.display`. An entry with neither has nothing to match and is dropped.
 */
export function complaintRefs(model: PatientComplaintModel | null | undefined): ChiefComplaintRef[] {
  const out: ChiefComplaintRef[] = [];
  for (const entry of model?.complaints ?? []) {
    if (!isComplaintEntry(entry)) continue;
    const code = entry.coding?.code?.trim() ?? '';
    const display = entry.coding?.display?.trim() ?? '';
    if (code || display) out.push({ code, display });
  }
  return out;
}
